import { WorkoutRuleDto } from '@api/FitnessActivityClient';
import { FormSection } from '@components/form-section';
import { useWrapRequest } from '@components/hooks/use-wrap-request';
import { Spinner } from '@components/spinner';
import { StickyBar } from '@components/sticky-bar';
import { TextField } from '@components/textfield';
import { Button } from '@components/ui/button';
import { Api } from '@core/api';
import { I18n } from '@core/i18n';
import { injectTSDI } from '@core/tsdi';
import { useStore } from './store';
import { observer } from 'mobx-react';
import React from 'react';

export const WorkoutRulesGlobalRules = observer(() => {
    const { fitnessActivityClient } = injectTSDI(Api);

    const currentWorkoutRules = useWrapRequest(async () => {
        const result =
            await fitnessActivityClient.admin.workoutRuleAdminControllerGetGlobalWorkoutRules();
        return result.data;
    });

    React.useEffect(() => {
        currentWorkoutRules.request();
    }, []);

    return (
        <>
            {currentWorkoutRules.match({
                loading: () => (
                    <div className="flex min-h-[400px] items-center justify-center">
                        <Spinner />
                    </div>
                ),
                fetched: (currentWorkoutRules) => (
                    <GeneralWorkoutRules
                        currentWorkoutRules={currentWorkoutRules}
                    />
                )
            })}
        </>
    );
});

interface GeneralWorkoutRulesFormProps {
    currentWorkoutRules: WorkoutRuleDto;
}

const GeneralWorkoutRules = observer(
    ({ currentWorkoutRules }: GeneralWorkoutRulesFormProps) => {
        const { fields, errors, onSubmitHandler, workoutRuleRequest } =
            useStore(currentWorkoutRules);
        const { __ } = injectTSDI(I18n);

        const {
            minWorkoutDurationMinutes,
            minAverageSpeed,
            maxAverageSpeed,
            maxAllowedSpeed,
            minStudioVisitDurationMinutes,
            noRecentCheckInConfirmationNotificationTimeoutMinutes,
            minConfirmationsAwayToCheckOut,
            minDurationAwayToCheckOutMinutes,
            distanceCheckOutThreshold,
            checkInTimeoutMinutes
        } = fields;

        return (
            <>
                <div className="flex max-w-[900px] flex-col gap-6 pb-24">
                    <FormSection title="Check-in">
                        <div className="grid gap-4 md:grid-cols-2">
                            <TextField
                                disabled={true}
                                value={0}
                                onChange={() => void null}
                                type="number"
                                label="Confirmations frequency"
                                suffix="min"
                                helperText
                            />
                            <TextField
                                value={distanceCheckOutThreshold.value}
                                onChange={distanceCheckOutThreshold.onChange}
                                type="number"
                                label="Auto check-out distance"
                                suffix="meters"
                                error={
                                    distanceCheckOutThreshold.error &&
                                    __(
                                        distanceCheckOutThreshold.error as I18nKey,
                                        0
                                    )
                                }
                                required
                                helperText
                            />
                            <TextField
                                value={minConfirmationsAwayToCheckOut.value}
                                onChange={
                                    minConfirmationsAwayToCheckOut.onChange
                                }
                                type="number"
                                label="Amount of away confirmations to check-out"
                                error={
                                    minConfirmationsAwayToCheckOut.error &&
                                    __(
                                        minConfirmationsAwayToCheckOut.error as I18nKey,
                                        0
                                    )
                                }
                                required
                                helperText
                            />
                            <TextField
                                value={
                                    noRecentCheckInConfirmationNotificationTimeoutMinutes.value
                                }
                                onChange={
                                    noRecentCheckInConfirmationNotificationTimeoutMinutes.onChange
                                }
                                type="number"
                                label="No recent confirmation notification timeout"
                                suffix="min"
                                error={
                                    noRecentCheckInConfirmationNotificationTimeoutMinutes.error &&
                                    __(
                                        noRecentCheckInConfirmationNotificationTimeoutMinutes.error as I18nKey,
                                        0
                                    )
                                }
                                required
                                helperText
                            />
                            <TextField
                                value={minDurationAwayToCheckOutMinutes.value}
                                onChange={
                                    minDurationAwayToCheckOutMinutes.onChange
                                }
                                type="number"
                                label="Minimum facility zone checkout timeout"
                                suffix="min"
                                error={
                                    minDurationAwayToCheckOutMinutes.error &&
                                    __(
                                        minDurationAwayToCheckOutMinutes.error as I18nKey,
                                        0
                                    )
                                }
                                required
                                helperText
                            />
                            <TextField
                                disabled={true}
                                value={0}
                                onChange={() => void null}
                                type="number"
                                label="Timed out checkout minimum duration"
                                suffix="min"
                                required
                                helperText
                            />
                            <TextField
                                value={checkInTimeoutMinutes.value}
                                onChange={checkInTimeoutMinutes.onChange}
                                type="number"
                                label="CheckIn no confirmation timeout"
                                suffix="min"
                                error={
                                    checkInTimeoutMinutes.error &&
                                    __(
                                        checkInTimeoutMinutes.error as I18nKey,
                                        0
                                    )
                                }
                                required
                                helperText
                            />
                            <TextField
                                value={minStudioVisitDurationMinutes.value}
                                onChange={
                                    minStudioVisitDurationMinutes.onChange
                                }
                                type="number"
                                label="Minimum studio visit duration"
                                suffix="min"
                                required
                                error={
                                    minStudioVisitDurationMinutes.error &&
                                    __(
                                        minStudioVisitDurationMinutes.error as I18nKey,
                                        0
                                    )
                                }
                                helperText
                            />
                        </div>
                    </FormSection>
                    {/* TODO: Remove this section */}
                    <FormSection title="Cycling">
                        <div className="grid gap-4 md:grid-cols-2">
                            <TextField
                                disabled={true}
                                value={minAverageSpeed.value}
                                onChange={minAverageSpeed.onChange}
                                type="number"
                                label="Minimum average speed"
                                suffix="km/h"
                                required
                                error={
                                    minAverageSpeed.error &&
                                    __(minAverageSpeed.error as I18nKey, 0)
                                }
                                helperText
                            />
                            <TextField
                                disabled={true}
                                value={maxAverageSpeed.value}
                                onChange={maxAverageSpeed.onChange}
                                type="number"
                                label="Maximum average speed"
                                suffix="km/h"
                                required
                                error={
                                    maxAverageSpeed.error &&
                                    __(maxAverageSpeed.error as I18nKey, 0)
                                }
                                helperText
                            />
                            <TextField
                                disabled={true}
                                value={maxAllowedSpeed.value}
                                onChange={maxAllowedSpeed.onChange}
                                type="number"
                                label="Maximum allowed speed"
                                suffix="km/h"
                                required
                                error={
                                    maxAllowedSpeed.error &&
                                    __(maxAllowedSpeed.error as I18nKey, 0)
                                }
                                helperText
                            />
                            <TextField
                                disabled={true}
                                value={minWorkoutDurationMinutes.value}
                                onChange={minWorkoutDurationMinutes.onChange}
                                type="number"
                                label="Minimum workout time"
                                suffix="min"
                                required
                                error={
                                    minWorkoutDurationMinutes.error &&
                                    __(
                                        minWorkoutDurationMinutes.error as I18nKey,
                                        0
                                    )
                                }
                                helperText
                            />
                        </div>
                    </FormSection>
                </div>

                <StickyBar>
                    <Button
                        onClick={onSubmitHandler}
                        disabled={Boolean(errors)}
                        loading={workoutRuleRequest.loading}
                    >
                        Save
                    </Button>
                </StickyBar>
            </>
        );
    }
);
